/**
 * Client-side advisories shown while typing (U3/U5); none blocks a save. The
 * server's lint (templates/lint.go) is authoritative and arrives after a save.
 */

/** copy: ours — the UI plan asks for the warning, not its words. */
export const SUBJECT_SHORT_WARNING = 'Subject is under 3 words — a fuller subject reads less like spam.';
export const SIGN_OFF_WARNING = 'Ends with a sign-off — the signature is added automatically';
export const SIGN_OFF_KEPT_FOR_FORMATTING = "Couldn't remove the sign-off without losing this step's formatting. Delete it from the message yourself.";
export const SIGNATURE_LINK_WARNING = 'Signature contains a link — keep links in the template body.';
export const SIGNATURE_MONEY_WARNING = 'Signature mentions money — keep figures in the template body.';

const MIN_SUBJECT_WORDS = 3;

export function subjectWordCount(subject: string): number {
  return subject.trim().split(/\s+/).filter(Boolean).length;
}

export function isShortSubject(subject: string): boolean {
  const n = subjectWordCount(subject);
  return n > 0 && n < MIN_SUBJECT_WORDS;
}

/** A whole-line closer, optionally followed by `{sender}`; tested on whitespace-collapsed lines. */
const SIGN_OFF_WORD =
  /^(?:(?:kind |warm |best )?regards|best|thanks|thank you|many thanks|cheers|warmly|sincerely|talk soon|looking forward(?: to (?:hearing from you|it))?)[,!.]?(?: \{sender\})?$/i;
const DASH = /^(?:—|--) ?/;
const DASH_SENDER = /^(?:—|--) ?\{sender\}$/;
const POSTSCRIPT = /^p\.?\s?s\b/i;
const NAME_TOKEN = /^(?:\{sender\}|\p{Lu}[\p{L}'’.,-]*)$/u;
const SEPARATOR = /^[·|&,-]$/;
const MAX_NAME_WORDS = 3;

// Collapsing whitespace first keeps every pattern here linear on long lines.
const norm = (line: string): string => line.trim().split(/\s+/).join(' ');

const isCloser = (line: string): boolean => SIGN_OFF_WORD.test(norm(line));

// Name-like means capitalised words or {sender}: no digits, currency or colons.
function isNameLike(text: string): boolean {
  if (POSTSCRIPT.test(text)) return false;
  const words = text.split(' ').filter((t) => t && !SEPARATOR.test(t));
  return words.length > 0 && words.length <= MAX_NAME_WORDS && words.every((t) => NAME_TOKEN.test(t));
}

function lastNonEmpty(lines: string[], from: number): number {
  let i = from;
  while (i >= 0 && !(lines[i] ?? '').trim()) i -= 1;
  return i;
}

// A capitalised dash line is often a list item ("— Reel"), so it only counts
// after a blank line, a closer, or at the start of the body — or as "— {sender}".
function isDashSignOff(lines: string[], at: number): boolean {
  const line = norm(lines[at] ?? '');
  if (DASH_SENDER.test(line)) return true;
  if (!DASH.test(line) || !isNameLike(line.replace(DASH, ''))) return false;
  if (at === 0 || !(lines[at - 1] ?? '').trim()) return true;
  return isCloser(lines[at - 1] ?? '');
}

/** Index of the first line of a trailing sign-off block, or -1. */
export function signOffLineIndex(body: string): number {
  const lines = body.split('\n');
  const last = lastNonEmpty(lines, lines.length - 1);
  if (last < 0) return -1;
  const lastLine = norm(lines[last] ?? '');
  // A bare "Thanks," names no one, so it is body text and the signature still goes below it.
  if (isCloser(lastLine)) return lastLine.includes('{sender}') ? last : -1;
  const prev = lastNonEmpty(lines, last - 1);
  const closerAbove = prev >= 0 && isCloser(lines[prev] ?? '');
  if (isDashSignOff(lines, last)) return closerAbove ? prev : last;
  if (lastLine === '{sender}') return closerAbove ? prev : last;
  // "Thanks,\nUbed" — a bare name under a closer.
  return closerAbove && isNameLike(lastLine) ? prev : -1;
}

/** The exact lines Remove would strip, "" when none. */
export function signOffText(body: string): string {
  const idx = signOffLineIndex(body);
  if (idx < 0) return '';
  return body
    .split('\n')
    .slice(idx)
    .map((l) => l.trim())
    .filter(Boolean)
    .join('\n');
}

export function stripSignOff(body: string): string {
  const idx = signOffLineIndex(body);
  if (idx < 0) return body;
  return body.split('\n').slice(0, idx).join('\n').trimEnd();
}

const LINK = /https?:\/\/|www\./i;
// Go's moneyRe (templates/lint.go) plus the suffix form "5000 inr".
const MONEY = /[₹$€£]\s*\d|\b(?:inr|usd|eur|gbp|rs\.?)\s*\d|\b\d[\d,.]*\s?(?:usd|inr|eur|gbp|aed|rs)\b/i;

/** The server lint code each client signature warning duplicates. */
export const SIGNATURE_WARNING_CODES: Record<string, string> = {
  [SIGNATURE_LINK_WARNING]: 'signature_has_link',
  [SIGNATURE_MONEY_WARNING]: 'signature_has_money',
};

export function signatureWarnings(signature: string): string[] {
  const out: string[] = [];
  if (LINK.test(signature)) out.push(SIGNATURE_LINK_WARNING);
  if (MONEY.test(signature)) out.push(SIGNATURE_MONEY_WARNING);
  return out;
}
